import { useEffect, useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { Star, StarBorder, Verified } from "@mui/icons-material";
import CommentActions from "@/components/CommentActions";
import UserProfileDrawer from "@/components/UserProfileDrawer";
import { toggleCommentFavorite } from "@/services/api";
import { formatSeconds } from "@/utils";
import { formatDate } from "@/utils/time";
import { renderTextWithMentions } from "@/utils/mentions";

export default function CommentCard({ event, content }) {
  const avatarSize = event.parent_id ? 28 : 34;
  const [favorite, setFavorite] = useState(false); // lets set in useEffect is event changes
  const [favoriteCount, setFavoriteCount] = useState(0); // lets set in useEffect is event changes

  function getCurrentTime() {
    if (content.type === "audio") {
      return event.data.current_time;
    }
    return undefined;
  }

  const handleFavorite = async () => {
    const newFavoriteState = !favorite;
    const newFavoriteCount = newFavoriteState
      ? favoriteCount + 1
      : favoriteCount - 1;

    // Optimistic update
    setFavorite(newFavoriteState);
    setFavoriteCount(newFavoriteCount);

    await toggleCommentFavorite(event);

    // Update the event prop so it persists across remounts
    event.user_favorite = newFavoriteState;
    event.favorite_count = newFavoriteCount;
  };

  // Sync state with event prop when it changes (e.g., after parent refetches)
  useEffect(() => {
    setFavorite(event.user_favorite);
    setFavoriteCount(event.favorite_count);
  }, [event.user_favorite, event.favorite_count]);

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <UserProfileDrawer
        userKey={event.user.key}
        userProfile={event.user.meta.profile}
        avatarSize={avatarSize}
      />

      <Box id="post-content-container" style={{ width: "100%" }}>
        <Box
          id="post-header"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginTop: "-5px",
          }}
        >
          <Box
            id="user-detail"
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              // dont show scrollbar
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::WebkitScrollbar": {
                display: "none",
              },
            }}
          >
            <Typography
              variant="body"
              style={{ marginRight: "3px", fontWeight: "bold" }}
            >
              {event.user.name}
            </Typography>

            {event.user.role == "artist" && (
              <Verified
                color={event.user.verified ? "secondary" : "disabled"}
                style={{ fontSize: "15px", marginRight: "5px" }}
              />
            )}

            {event.user.role == "user" && event.user.verified && (
              <Verified
                color="primary"
                style={{ fontSize: "15px", marginRight: "5px" }}
              />
            )}

            <Typography variant="caption" sx={{ mx: 0.5, color: "lightgray" }}>
              {formatDate(event.created)}
            </Typography>

            {getCurrentTime() !== undefined && (
              <Typography
                variant="caption"
                style={{ margin: "3px 3px 0 3px", color: "lightgray" }}
              >
                (at {formatSeconds(getCurrentTime())})
              </Typography>
            )}

            {event.artist_favorite && (
              <Box>
                <Typography
                  variant="caption"
                  style={{
                    color: "lightgray",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  •
                  <Star
                    color="secondary"
                    style={{ fontSize: "15px", margin: "0 2px" }}
                  />
                  by artist
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box id="post-content" style={{ marginTop: "-5px" }}>
          {/* needs to wrap long comment */}
          <Box style={{ wordWrap: "break-all", whiteSpace: "pre-wrap" }}>
            <Typography variant="body" style={{ marginRight: "5px" }}>
              {renderTextWithMentions(event.data.text)}
            </Typography>
          </Box>
        </Box>
        <CommentActions event={event} content={content} />
      </Box>

      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginLeft: "10px",
        }}
      >
        <IconButton size="small" onClick={handleFavorite} sx={{ p: 0 }}>
          {favorite ? (
            <Star color="secondary" />
          ) : (
            <StarBorder style={{ color: "lightgray" }} />
          )}
        </IconButton>
        <Typography
          variant="caption"
          style={{ color: "lightgray", marginTop: "2px" }}
        >
          {favoriteCount > 0 ? favoriteCount : ""}
        </Typography>
      </Box>
    </Stack>
  );
}
